import type { ColumnsType } from 'antd/es/table';
import { Select } from 'antd';
import {
  Box,
  Colors,
  IcClose,
  IcCopy,
  IcDevice,
  IcEdit,
  IcMenuChange,
  IcScreen,
  IcTick,
  IcTrash,
  Pagination,
  Row,
  StatusTag,
  Table,
  Text,
} from 'basic-ui-lib';
import { ActionProps } from 'basic-ui-lib/components/Table/Table';
import { IScreen, IScreenDetail } from 'modules/dashboard/interfaces/api';
import dashboardApis from 'modules/dashboard/services/apis';
import { ScreenStatus, StatusScreenConfigs } from 'modules/dashboard/services/constants';
import useGetListPlaylists from 'modules/playlist/services/hooks/useGetListPlaylists';
import useGetScreensInStore from 'modules/store/services/hooks/useListScreensInStore';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getMessageError } from 'services/response';
import styled from 'styled-components';
import toast from 'utils/toast';
import ModalConfirm, { useModalConfirmRef } from 'widgets/ModalConfirm';
import { setPageLoading } from 'widgets/PageLoading';

const StoreScreenTable = () => {
  const { id } = useParams<{ id: string }>();
  const modalConfirmRef = useModalConfirmRef();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);

  const { screensListInStore, mutate } = useGetScreensInStore(id || '');
  const [playlists, , fetchPlaylists] = useGetListPlaylists(true);

  useEffect(() => {
    fetchPlaylists({ page: 1, size: 100 });
  }, []);

  const playlistOptions = [
    { label: 'None', value: '' },
    ...(playlists?.content?.map(p => ({ label: p.name, value: p.id })) ?? []),
  ];

  const columns: ColumnsType<IScreen> = [
    {
      title: 'SCREEN',
      dataIndex: 'playerName',
      render: (text, record) => (
        <Row gap={12} alignItems='center'>
          <Box
            display='flex'
            justifyContent='center'
            alignItems='center'
            width={40}
            height={40}
            backgroundColor={Colors.neutral.bg[2]}
          >
            <IcScreen width={24} height={24} fill={Colors.neutral.bg[7]} />
          </Box>
          <div>
            <Text content_2 color='primary.6'>
              {text}
            </Text>
            {record.code && (
              <Text body_1 color='neutral.text.3'>
                {record.code}
              </Text>
            )}
          </div>
        </Row>
      ),
    },
    {
      title: 'STATUS',
      dataIndex: 'status',
      render: (status) => {
        const config = StatusScreenConfigs[status as ScreenStatus];
        return (
          <StatusTag
            label={config?.label ?? status}
            backgroundColor={config?.backgroundColor}
            color={config?.color}
          />
        );
      },
    },
    {
      title: 'RESOLUTION',
      dataIndex: 'resolution',
    },
    {
      title: 'PLAYLISTS',
      dataIndex: 'playListId',
      render: (playListId: string | null, record) => (
        <PlaylistSelect
          value={playListId ?? ''}
          placeholder='None'
          options={playlistOptions}
          popupMatchSelectWidth={false}
          onChange={(val) => handleUpdatePlaylist(record, val as string)}
        />
      ),
    },
    {
      title: 'LAST ACTIVE',
      dataIndex: 'lastTimeActive',
    },
  ];

  const actions: ActionProps<IScreen>[] = [
    {
      label: 'Refresh',
      icon: <IcMenuChange width={16} height={16} />,
      onClick: (item) => handleRefreshScreen(item.id),
    },
    {
      label: 'Copy Link',
      icon: <IcCopy width={16} height={16} />,
      onClick: (item) => {
        navigator.clipboard.writeText(item.id);
        toast.success('Copied to clipboard');
      },
    },
    {
      label: 'Edit Name',
      icon: <IcEdit width={16} height={16} />,
      onClick: (item) => console.log('Edit Name', item.id),
    },
    {
      label: 'Edit Content',
      icon: <IcDevice width={16} height={16} />,
      onClick: (item) => navigate(`/screen/${item.id}`),
    },
    {
      label: (item) => (item.status === ScreenStatus.ONLINE ? 'Deactivate' : 'Reactivate'),
      icon: (item) =>
        item.status === ScreenStatus.ONLINE ? (
          <IcClose width={16} height={16} />
        ) : (
          <IcTick width={16} height={16} />
        ),
      onClick: (item) => handleChangeStatus(item),
      hidden: (item) => item.status === ScreenStatus.DRAFT,
    },
    {
      label: 'Delete Screen',
      icon: <IcTrash width={16} height={16} />,
      onClick: (item) => onDeleteScreen(item.id),
    },
  ];

  const handleUpdatePlaylist = async (screen: IScreen, newPlaylistId: string) => {
    try {
      setPageLoading(true);
      const detailRes = await dashboardApis.getScreenDetail(screen.id);
      const detail: IScreenDetail = detailRes?.data?.data;
      if (!detail) return;
      await dashboardApis.updateScreen({
        id: detail.id,
        deviceId: detail.deviceInfo?.deviceId ?? '',
        storeId: detail.storeId ?? '',
        screenName: detail.screenName,
        playListId: newPlaylistId,
        layoutCode: detail.layoutCode ?? '',
        status: detail.status,
        note: detail.note,
        duration: detail.duration,
        contentItems: detail.contentItems ?? [],
      });
      toast.success('Playlist updated successfully');
      mutate({ page: currentPage, size: 10 });
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to update playlist');
    } finally {
      setPageLoading(false);
    }
  };

  const handleRefreshScreen = async (screenId: string) => {
    try {
      setPageLoading(true);
      const res = await dashboardApis.refreshScreen(screenId);
      if (res?.data?.data) {
        toast.success('Screen refreshed successfully');
        mutate({ page: currentPage, size: 10 });
      }
    } catch (error) {
      toast.error(getMessageError(error));
    } finally {
      setPageLoading(false);
    }
  };

  const handleDeleteScreen = async (screenId: string) => {
    try {
      setPageLoading(true);
      const res = await dashboardApis.deleteScreen(screenId);
      if (res?.data?.data) {
        toast.success('Screen deleted successfully');
        mutate({ page: currentPage, size: 10 });
      }
    } catch (error) {
      toast.error(getMessageError(error));
    } finally {
      setPageLoading(false);
    }
  };

  const onDeleteScreen = (screenId: string) => {
    modalConfirmRef.current?.open(() => handleDeleteScreen(screenId), {
      message: 'Are you sure you want to delete this screen? This action cannot be undone.',
    });
  };

  const handleChangeStatus = async (item: IScreen) => {
    try {
      const newStatus =
        item.status === ScreenStatus.ONLINE ? ScreenStatus.DEACTIVATED : ScreenStatus.ONLINE;
      const res = await dashboardApis.updateStatusScreen({ id: item.id, status: newStatus });
      if (res?.data?.data) {
        toast.success(
          item.status === ScreenStatus.ONLINE
            ? 'Screen deactivated successfully'
            : 'Screen reactivated successfully',
        );
        mutate({ page: currentPage, size: 10 });
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to update screen status');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    mutate({ page, size: 10 });
  };

  return (
    <TableBox>
      <Table<IScreen>
        className='custom-table'
        columns={columns}
        dataSource={screensListInStore?.content ?? []}
        scroll={{ x: 'max-content' }}
        pagination={false}
        actions={actions}
      />
      <Pagination
        totalItems={screensListInStore?.totalElements ?? 0}
        itemsPerPage={10}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        labelDisplay='screens'
        showRowPerPage={true}
        maxVisiblePages={3}
      />
      <ModalConfirm ref={modalConfirmRef} />
    </TableBox>
  );
};

export default StoreScreenTable;

const TableBox = styled.div`
  width: 100%;
`;

const PlaylistSelect = styled(Select)`
  width: 160px;

  && .ant-select-selector {
    border-radius: 8px;
    font-family: 'Poppins';
    font-size: 14px;
    background: ${Colors.primary[1]};
  }

  && .ant-select-selection-item {
    font-family: 'Poppins';
    font-size: 14px;
    font-weight: 500;
    color: ${Colors.neutral.text[5]};
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;
